<!--@format-->
<template>
    <div class="mdf-guideline-widget" :class="componentClasses">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div class="description-container">{{ description }}</div>
                <app-text-editor
                    id="guideline-widget"
                    v-model="value"
                    tag="textarea"
                    color-picker-class="magic-branding"
                    :config="froalaConfig"
                    :show-branding-colors="false"
                />
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="close-button" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="handleSave"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppTextEditor = () => import('@/components/common/app-text-editor');

import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { responsiveHelpers } from '@/scripts/helpers';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'mdf-guideline-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-text-editor': AppTextEditor,
    },

    mixins: [ResponsiveMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            value: '',
        };
    },

    computed: {
        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },

        title() {
            const { options } = this.widget;
            return options.title;
        },

        description() {
            const { options } = this.widget;
            return options.description;
        },

        type() {
            const { options } = this.widget;
            return options.type;
        },

        currentValue() {
            const { options } = this.widget;
            return options.value;
        },

        froalaConfig() {
            return {
                charCounterCount: false,
                placeholderText: 'Click here to start typing...',
                key: this.$helpers.getFroalaKey(),
                paragraphFormatSelection: false,
                pasteAllowedStyleProps: ['color'],
                fontFamilySelection: false,
                fontSizeSelection: false,
                toolbarButtons: [
                    'emoticons',
                    'formatUL',
                    'bold',
                    'italic',
                    'colorPicker',
                    'insertLink',
                    'undo',
                    'redo',
                ],
            };
        },

        isMasterAgencyUser() {
            return this.$store.getters.isMasterAgencyUser;
        },

        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isMdfRequestSettingsManager() {
            return (
                this.isMasterAgencyUser ||
                this.isOrganizationUser ||
                this.$store.state.user.roles.includes('MdfRequestSettingsManager')
            );
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    created() {
        this.validateRoles();
        this.value = this.currentValue;
    },

    methods: {
        validateRoles() {
            if (!this.isMdfRequestSettingsManager) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSave() {
            var payload = {
                data: this.value,
                type: this.type,
            };
            EventBus.$emit('mdf-add-activity-type:save', payload);
            this.closeWidget();
        },
    },
};
</script>

<style>
.mdf-guideline-widget.widget .content-wrapper {
    padding: 24px;
    display: flex;
    flex-direction: column;
}

.mdf-guideline-widget .content-wrapper .description-container {
    margin-bottom: 24px;
}

.mdf-guideline-widget .app-button.normal {
    padding: 12px 60px;
}

.mdf-guideline-widget .app-button.normal.close-button {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
}

.mdf-guideline-widget .app-button.normal.close-button:hover {
    background: #e1e0e5;
    color: #382153;
}

.mdf-guideline-widget .content-wrapper .app-text-editor {
    border: 1px solid #cac6cb;
    border-radius: 20px;
    flex-grow: 1;
    margin-bottom: 8px;
}

.mdf-guideline-widget .content-wrapper .app-text-editor .fr-toolbar {
    align-items: center;
    background: transparent linear-gradient(265deg, #e38dee 0%, #7a5bff 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px 20px 0px 0px;
    column-gap: 8px;
    display: flex;
    flex-wrap: wrap;
    padding: 12px 24px;
}

.mdf-guideline-widget
    .content-wrapper
    .app-text-editor
    .app-text-editor-wrapper
    .fr-box
    .fr-wrapper {
    border-radius: 0px 0px 20px 20px;
}

.mdf-guideline-widget .content-wrapper #guideline-widget .fr-toolbar .fr-command.fr-btn {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.mdf-guideline-widget .content-wrapper .fr-toolbar .fr-command.fr-btn img {
    margin: 0;
    width: 24px;
}

.mdf-guideline-widget .content-wrapper .fr-toolbar [id*='emoticons'].fr-command.fr-btn img {
    margin: 0;
    width: 18px;
}

.mdf-guideline-widget.medium .content-wrapper .fr-toolbar [id*='insertLink'].fr-command.fr-btn {
    margin-right: calc(100% - 403px);
}

.mdf-guideline-widget .content-wrapper .fr-toolbar .fr-command.fr-btn i {
    font-size: 16px;
}

.mdf-guideline-widget .content-wrapper #guideline-widget .fr-toolbar .fr-buttons {
    display: flex;
}

.mdf-guideline-widget
    .content-wrapper
    #guideline-widget
    .fr-toolbar
    .fr-buttons
    button:nth-child(1) {
    display: none;
}

.mdf-guideline-widget
    .content-wrapper
    .app-text-editor
    .app-text-editor-wrapper
    .fr-box.fr-basic
    .fr-element {
    color: #382153;
}
</style>
